import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useHotel } from '@/context/HotelContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Sparkles, CheckCircle2, Clock, Loader2 } from 'lucide-react';
import { getErrorMessage } from '@/lib/errorUtils';

import { API } from '@/config/api';

const HousekeepingPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const { rooms, roomTypes } = useHotel();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState('');

  useEffect(() => {
    fetchTasks();
  }, [user?.hotel_id]);

  const fetchTasks = async () => {
    if (!user?.hotel_id) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/hotels/${user.hotel_id}/housekeeping`, {
        headers: getAuthHeaders()
      });
      setTasks(response.data);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${API}/hotels/${user.hotel_id}/housekeeping`,
        { room_id: selectedRoom, task_type: 'cleaning' },
        { headers: getAuthHeaders() }
      );
      
      setTasks(prev => [response.data, ...prev]);
      setShowDialog(false);
      setSelectedRoom('');
      toast.success('Cleaning task created');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create task'));
    }
  };

  const handleUpdateStatus = async (taskId, status) => {
    try {
      await axios.put(
        `${API}/hotels/${user.hotel_id}/housekeeping/${taskId}?status=${status}`,
        {},
        { headers: getAuthHeaders() }
      );
      
      setTasks(prev => prev.map(t => 
        t.id === taskId ? { ...t, status } : t
      ));
      toast.success('Task updated');
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const getRoomNumber = (roomId) => {
    const room = rooms.find(r => r.id === roomId);
    return room?.room_number || 'N/A';
  };

  const getRoomTypeName = (roomId) => {
    const room = rooms.find(r => r.id === roomId);
    if (room) {
      const rt = roomTypes.find(t => t.id === room.room_type_id);
      return rt?.name || '';
    }
    return '';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="h-5 w-5 text-emerald-500" />;
      case 'in_progress':
        return <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />;
      default:
        return <Clock className="h-5 w-5 text-amber-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700',
      in_progress: 'bg-blue-100 text-blue-700',
      completed: 'bg-emerald-100 text-emerald-700'
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  const dirtyRooms = rooms.filter(r => r.status === 'dirty');
  const pendingTasks = tasks.filter(t => t.status === 'pending');
  const inProgressTasks = tasks.filter(t => t.status === 'in_progress');
  const completedToday = tasks.filter(t => 
    t.status === 'completed' && 
    t.completed_at?.slice(0, 10) === new Date().toISOString().slice(0, 10)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="housekeeping-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Outfit']">Housekeeping</h1>
          <p className="text-muted-foreground">Manage cleaning tasks and room status</p>
        </div>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button data-testid="create-task-btn" disabled={dirtyRooms.length === 0}>
              <Plus className="h-4 w-4 mr-2" />
              New Cleaning Task
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Cleaning Task</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateTask} className="space-y-4">
              <div className="space-y-2">
                <Label>Select Room *</Label>
                <Select value={selectedRoom} onValueChange={setSelectedRoom}>
                  <SelectTrigger data-testid="task-room-select">
                    <SelectValue placeholder="Select a dirty room" />
                  </SelectTrigger>
                  <SelectContent>
                    {dirtyRooms.map((room) => (
                      <SelectItem key={room.id} value={room.id}>
                        Room {room.room_number} - {getRoomTypeName(room.id)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full" data-testid="submit-task-btn" disabled={!selectedRoom}>
                Create Task
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Dirty Rooms</p>
                <p className="text-2xl font-bold text-amber-600">{dirtyRooms.length}</p>
              </div>
              <Sparkles className="h-8 w-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending Tasks</p>
                <p className="text-2xl font-bold">{pendingTasks.length}</p>
              </div>
              <Clock className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">In Progress</p>
                <p className="text-2xl font-bold text-blue-600">{inProgressTasks.length}</p>
              </div>
              <Loader2 className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Completed Today</p>
                <p className="text-2xl font-bold text-emerald-600">{completedToday.length}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Task Board */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pending */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock className="h-5 w-5 text-amber-500" />
              Pending ({pendingTasks.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {pendingTasks.map((task) => (
              <div
                key={task.id}
                className="p-4 border rounded-lg bg-amber-50/50 dark:bg-amber-950/20"
                data-testid={`pending-task-${task.id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">Room {getRoomNumber(task.room_id)}</span>
                  <Badge className={getStatusBadge(task.status)}>Pending</Badge>
                </div>
                <p className="text-sm text-muted-foreground mb-3">{getRoomTypeName(task.room_id)}</p>
                <Button
                  size="sm"
                  className="w-full"
                  onClick={() => handleUpdateStatus(task.id, 'in_progress')}
                  data-testid={`start-task-${task.id}`}
                >
                  Start Cleaning
                </Button>
              </div>
            ))}
            {pendingTasks.length === 0 && (
              <p className="text-center text-muted-foreground py-4">No pending tasks</p>
            )}
          </CardContent>
        </Card>

        {/* In Progress */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Loader2 className="h-5 w-5 text-blue-500" />
              In Progress ({inProgressTasks.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {inProgressTasks.map((task) => (
              <div
                key={task.id}
                className="p-4 border rounded-lg bg-blue-50/50 dark:bg-blue-950/20"
                data-testid={`progress-task-${task.id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">Room {getRoomNumber(task.room_id)}</span>
                  <Badge className={getStatusBadge(task.status)}>In Progress</Badge>
                </div>
                <p className="text-sm text-muted-foreground mb-3">{getRoomTypeName(task.room_id)}</p>
                <Button
                  size="sm"
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => handleUpdateStatus(task.id, 'completed')}
                  data-testid={`complete-task-${task.id}`}
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Mark Complete
                </Button>
              </div>
            ))}
            {inProgressTasks.length === 0 && (
              <p className="text-center text-muted-foreground py-4">No tasks in progress</p>
            )}
          </CardContent>
        </Card>

        {/* Completed Today */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <CheckCircle2 className="h-5 w-5 text-emerald-500" />
              Completed Today ({completedToday.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {completedToday.map((task) => (
              <div
                key={task.id}
                className="p-4 border rounded-lg bg-emerald-50/50 dark:bg-emerald-950/20"
                data-testid={`completed-task-${task.id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">Room {getRoomNumber(task.room_id)}</span>
                  <Badge className={getStatusBadge(task.status)}>Completed</Badge>
                </div>
                <p className="text-sm text-muted-foreground">{getRoomTypeName(task.room_id)}</p>
              </div>
            ))}
            {completedToday.length === 0 && (
              <p className="text-center text-muted-foreground py-4">No completed tasks today</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default HousekeepingPage;
